<template>
   <div class="request-source-form src-code">
      <div v-if="!submitSuccess">
         <div class="h2 text-center form-header">
            Download the source code of this dashboard
         </div>
         <form @submit.prevent="onSubmit">
            <Name ref="nameComponent" :value="form.name" :options="fieldOptions" />
            <Email ref="emailComponent" :value="form.email" :options="fieldOptions" />
            <Phone ref="phoneComponent" :value="form.phone" :options="fieldOptions" />
            <Company ref="companyComponent" :value="form.company" :options="fieldOptions" />
            <Country ref="countryComponent" :value="form.country" />

            <Select
               ref="selectUserTypeComponent"
               :options="[
                  'Developer and want the source code of this dashboard',
                  'Product Manager and want to know how to get this dashboard built',
                  'Business User (e.g. Business Analyst/ CEO) and want someone to give me a ready made dashboard',
               ]"
               place-holder="I am a"
               invalid-feedback="We’d really like to know about you."
               valid-feedback="Sounds good."
               data-name="userType"
            />

            <button
               type="submit"
               :disabled="btnLoading"
               class="btn btn-primary-grad btn-block align-center shadow-sm"
            >
               <i v-if="btnLoading" class="loadingSpinner" />
               <span>Submit</span>
            </button>
            <div class="d-flex align-items-center justify-content-center pt-1">
               <span class="ml-1 small text-muted"
                  >By signing up you agree to FusionCharts
                  <b-link href="/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</b-link>
               </span>
            </div>
         </form>
      </div>
      <div v-if="submitSuccess">
         <div class="text-center">
            <div class="icon-container">
               <i class="fc_done" />
            </div>
            <h1 class="mb-1">Thank you for showing interest.</h1>
            <p class="subheading">
               If the download hasn't started automatically, please click
               <a id="dashboardDownloadLink" :href="dashboardDownloadUrl">here</a> to download
               manually.
            </p>
            <p class="subheading led">
               After the download is complete, please unzip the file to see the code for the entire
               dashboard. If you're not a developer and need help with implementing this dashboard,
               please <a href="#" @click="openDaaSModal">reach out to us.</a>
            </p>
         </div>
      </div>
   </div>
</template>

<script>
import { mapState } from 'vuex';
import $service from '../../util/service.js';
import Name from './elements/Name';
import Email from './elements/Email';
import Country from './elements/Country';
import Phone from './elements/Phone';
import Company from './elements/Company';
import Select from './elements/Select';

export default {
   components: {
      Name,
      Email,
      Country,
      Phone,
      Company,
      Select,
   },
   props: {
      dashboardName: {
         type: String,
         required: true,
      },
      slug: {
         type: String,
         required: true,
      },
   },

   data() {
      return {
         action: 'contact-for-source-codes',
         attemptSubmit: false,
         btnLoading: false,
         form: {
            name: '',
            email: '',
            phone: '',
            company: '',
            country: '',
            userType: '',
            product: 'dashboard',
            form: 'dashboardDownload',
         },
         submitSuccess: false,
         ...(process.browser && { tracker: this.$tracker.tracker }),
      };
   },

   computed: {
      ...mapState(['csrfToken']),

      fieldOptions() {
         return {
            // name: this.dashboardName,
            // mixpanelEve: this.product !== 'dashboard' && 'Data Story Form Interacted',
         };
      },

      dashboardDownloadUrl() {
         return `https://www.fusioncharts.com/demos/dashboards/${this.slug}.zip`;
      },
   },

   mounted() {
      this.$root.$on('bv::show::modal', () => {
         this.submitSuccess = false;
         this.btnLoading = false;
         this.attemptSubmit = false;
      });
   },

   methods: {
      updateFormData() {
         this.form.name = this.$refs.nameComponent.form.name;
         this.form.email = this.$refs.emailComponent.form.email;
         this.form.country = this.$refs.countryComponent.form.country;
         this.form.phone = this.$refs.phoneComponent.form.phone;
         this.form.company = this.$refs.companyComponent.form.company;
         this.form.userType = this.$refs.selectUserTypeComponent.form.data;
      },

      validateFormData() {
         this.$refs.emailComponent.validateMailData();
         this.$refs.nameComponent.validateNameData();
         this.$refs.countryComponent.validateCountryData();
         this.$refs.phoneComponent.validatePhoneData();
         this.$refs.companyComponent.validateCompanyData();
         this.$refs.selectUserTypeComponent.validateFieldData();
      },

      async onSubmit() {
         this.attemptSubmit = true;
         // validation and form update
         this.updateFormData();
         this.validateFormData();
         // setting data for further operations
         this.form._csrf = this.csrfToken;
         this.form.dashboardName = this.dashboardName;
         this.form.dashboardSlug = this.slug;

         if (!this.missingName) return;
         if (this.isValidEmail) return;
         if (this.isNotEmpty(this.form.country)) return;
         if (this.isNotEmpty(this.form.company)) return;
         if (this.isNotEmpty(this.form.userType)) return;

         this.btnLoading = true;

         try {
            const response = await $service.postDB(this.action, this.form);
            if (response.status === 200) {
               this.submitSuccess = true;

               window.dataLayer = window.dataLayer || [];
               window.dataLayer.push({
                  event: 'dashboardsourcecodeSuccess',
               });

               this.$emit('postSubmit', {
                  status: true,
                  values: this.form,
               });

               this.tracker('Dashboard Source Code Requested', {
                  Name: this.dashboardName,
               });

               this.$nextTick(() => {
                  // trigger download btn
                  const ele = document.getElementById('dashboardDownloadLink');
                  ele && ele.click();
               });
            }
         } catch (e) {
            // eslint-disable-next-line
            console.log('fail', e);
         }
      },

      openDaaSModal(e) {
         e.preventDefault();
         this.$root.$emit('bv::hide::modal', 'dashboardDownloadModal');
         this.$root.$emit('bv::show::modal', 'getInTouchModal');
      },
   },
};
</script>

<style lang="scss" scoped>
.request-source-form {
   max-width: 575px;
   margin: 0 auto;

   .btn-close {
      position: absolute;
      right: 0px;
      top: 0px;
      color: #9b9cb0;
      background-color: transparent;
   }
   .form-header {
      margin-bottom: 42px;
   }

   .icon-container {
      i {
         font-size: 10rem;
         color: #62be93;
      }
   }
}
</style>
